// Polyfill Object.hasOwn if it doesn't exist already.
import 'core-js/modules/es.object.has-own';
import 'core-js/modules/web.structured-clone';
import '../public/css/app.less';

import * as Sentry from '@sentry/vue';
import HighchartsVue from 'highcharts-vue';
import sanitizeHTML from 'sanitize-html';
import { createApp } from 'vue';
import VueClipboard from 'vue-clipboard2';

import 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/accessibility';
import 'highcharts/modules/drilldown';
import 'highcharts/modules/heatmap';
import 'highcharts/modules/pattern-fill';
import 'highcharts/modules/solid-gauge';
import 'highcharts/modules/treemap';

import { initializeAggConnect } from '@pendo/agg-connect';
import { PendoAnalytics } from '@pendo/components';
import { BuildingBlock } from '@pendo/services/BuildingBlocks';
import { baseUrl, gcsBucket, origin } from '@pendo/services/Environments';

import { FlagsPlugin } from '@/plugins/flags';
import { isProdEnvironment } from '@/settings/environment.utils.js';
import { setupAnalytics } from '@/utils/analytics';
import { setupPerfume } from '@/utils/browser-performance-analytics';
import { listenToDesigner } from '@/utils/designer-listener.js';
import { initializeHttpWrapper } from '@/utils/http';
import { i18n } from '@/utils/i18n';
import { initSentryByActiveSub } from '@/utils/sentry';
import { initAutoReportTracker } from '@/utils/track-report';
import { isZendeskEnabledForPath, setupZendeskSnippetForPendoInternal } from '@/utils/zendesk-setup';

import router from './vue-router/router';

import pendoGlobalsPlugin from '@/plugins/app-wide-dependency-plugin';
import { EnablementPlugin } from '@/plugins/enablement-plugin.js';

// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';

import App from './app.vue';

window.designerHost = __PROCESS__.LOCAL_ENV ? '' : window.location.origin;

const http = initializeHttpWrapper();
initializeAggConnect({
    http,
    env: isProdEnvironment() ? 'production' : window.appEnvId,
});

try {
    if (window.Cypress) store.dispatch('auth/fetchUnauthenticatedTrainingAttributes');
    else await store.dispatch('auth/fetchUnauthenticatedTrainingAttributes');
} catch (error) {
    console.error('Failed to fetch unauthenticated training attributes:', error);
}
store.dispatch('auth/init');
store.dispatch('config/init');

if (window.Cypress) {
    // Attach the store and permissions to the window, which can be useful
    // for manually mocking state or permissions in tests
    window.__store__ = store;
    window.__pendoPermissions__ = require('@pendo/permissions');
}

setupAnalytics();
setupPerfume();
listenToDesigner(store);
BuildingBlock.setDependencies(origin(), gcsBucket(), baseUrl());

if (!window.Cypress && isZendeskEnabledForPath(window.location.pathname)) {
    setupZendeskSnippetForPendoInternal();
}

// re-implement angular's a tag prevent default, so `<a href="#">` doesn't reload the page
document.addEventListener('click', (event) => {
    const aTag = event?.target?.closest('a');
    if (!aTag) return;

    const targetingHash = aTag.getAttribute('href') === '#';

    if (!targetingHash) return;

    event.preventDefault();
});

VueClipboard.config.autoSetContainer = true;

initAutoReportTracker(router);
if (!__PROCESS__.LOCAL_ENV) {
    initSentryByActiveSub(store, router);
}

let vueApp = null;

async function initVueApp () {
    const { default: vuetify } = await import('@portal/plugins/vuetify');

    vueApp = createApp(App);

    vueApp.config.silent = Boolean(!__PROCESS__.LOG_VUE_ERRORS);
    vueApp.config.globalProperties.$sanitize = sanitizeHTML;

    // Config change needed to make our Vue app work with the error clicks frustration type in session replay
    // See https://pendo-io.atlassian.net/wiki/spaces/ENG/pages/3446964272/Error+Clicks+Design+Doc#%E2%AD%90-Option-%233%3A-Error-event-listener-%2B-modify-Vue-config-%E2%AD%90

    vueApp.config.errorHandler = function (error, vm, info) {
        if (vm) {
            window.dispatchEvent(new CustomEvent('error', { detail: { element: vm.$el, error } }));
            Sentry.captureException(error, {
                tags: {
                    'vue.component': vm?.$options?.name || 'UnknownComponent',
                    'vue.errorContext': info,
                },
            });
        } else {
            window.dispatchEvent(new CustomEvent('error', { detail: { error } }));
            Sentry.captureException(error);
        }
        if (__PROCESS__.LOCAL_ENV) {
            console.error(error);
            throw error;
        }
    };

    vueApp.directive('pendo-analytics', PendoAnalytics);
    vueApp.use(VueClipboard);
    vueApp.use(pendoGlobalsPlugin);
    vueApp.use(EnablementPlugin, { store });
    vueApp.use(FlagsPlugin);
    vueApp.use(HighchartsVue);
    vueApp.use(store);
    vueApp.use(vuetify);
    vueApp.use(router);
    vueApp.use(i18n);

    if (__PROCESS__.LOCAL_ENV) {
        const params = new URLSearchParams(window.location.search);
        const feDebug = params.get('feDebug');

        if (__PROCESS__.FE_DEBUG || feDebug === 'true') {
            try {
                const VueScan = await import('z-vue-scan');
                vueApp.use(VueScan.default, {});
            } catch (error) {
                console.error('Error initializing VueScan', error);
            }
        }
    }

    if (window.Cypress) {
        vueApp.mixin({
            mounted () {
                this.$el.__vue__ = this;
            },
        });
    }

    router.isReady().then(() => {
        vueApp.mount('#vue-mount');
    });
}

async function bootstrapTheApp () {
    const unwatch = store.watch(store.getters['auth/isLoaded'], () => {
        initVueApp();
        unwatch();
    });
}

bootstrapTheApp();

// @vue/compat's native HMR doesn't work, so we force app re-render
if (module.hot) {
    window.__hmrForceRerender = () => {
        if (vueApp) {
            vueApp.unmount();
            initVueApp();
        }
    };

    /* eslint-disable-next-line no-undef */
    const runtime = typeof __VUE_HMR_RUNTIME__ !== 'undefined' ? __VUE_HMR_RUNTIME__ : null;
    if (runtime?.rerender && !runtime.__patched) {
        const originalRerender = runtime.rerender;
        const originalReload = runtime.reload;

        runtime.rerender = function (id, newRender) {
            originalRerender.call(this, id, newRender);
            window.__hmrForceRerender();
        };

        runtime.reload = function (id, newComponent) {
            originalReload.call(this, id, newComponent);
            window.__hmrForceRerender();
        };

        runtime.__patched = true;
    }
}
